'use client';

import { useEffect } from 'react';

/**
 * A utility component that prevents unwanted zoom on input fields in mobile browsers.
 * This is especially useful for iOS Safari which zooms on inputs with font-size < 16px.
 *
 * Add this component to your layout or providers for global effect.
 */
export function InputZoomPreventer() {
  useEffect(() => {
    // Only apply on iOS devices
    const IS_IOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!IS_IOS) return;

    // Cache viewport meta tag
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalContent = viewportMeta?.getAttribute('content') || '';

    // Function to set viewport to prevent zoom
    const disableZoom = () => {
      if (!viewportMeta) {
        viewportMeta = document.createElement('meta');
        (viewportMeta as HTMLMetaElement).name = 'viewport';
        document.head.appendChild(viewportMeta);
      }

      // Set strict no-zoom viewport
      viewportMeta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
      );
    };

    // Function to restore original viewport
    const restoreViewport = () => {
      if (viewportMeta && originalContent) {
        viewportMeta.setAttribute('content', originalContent);
      }
    };

    // Add event listeners to all input types that might trigger zoom
    const inputSelector = 'input, select, textarea';
    const inputs = document.querySelectorAll(inputSelector);

    // Attach listeners to existing inputs
    inputs.forEach((input) => {
      input.addEventListener('focus', disableZoom);
      input.addEventListener('blur', restoreViewport);
    });

    // Set up a mutation observer to handle dynamically added inputs
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // ELEMENT_NODE
              const newInputs = (node as Element).querySelectorAll?.(
                inputSelector
              );
              if (newInputs) {
                newInputs.forEach((input) => {
                  input.addEventListener('focus', disableZoom);
                  input.addEventListener('blur', restoreViewport);
                });
              }

              // Check if the node itself is an input
              if ((node as Element).matches?.(inputSelector)) {
                node.addEventListener('focus', disableZoom);
                node.addEventListener('blur', restoreViewport);
              }
            }
          });
        }
      });
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Clean up event listeners when component unmounts
    return () => {
      inputs.forEach((input) => {
        input.removeEventListener('focus', disableZoom);
        input.removeEventListener('blur', restoreViewport);
      });

      observer.disconnect();
      restoreViewport();
    };
  }, []);

  // This component doesn't render anything
  return null;
}

export default InputZoomPreventer;
